"use client";

import Link from "next/link";
import Image from "next/image";
import { useEffect, useRef } from "react";
import { Skeleton } from "../ui/skeleton";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { ProjectQueryParams } from "@/hooks/project/useGetProjects";
import { EmptyState } from "../ui/empty-state";
import { ErrorState } from "../ui/error-state";
import { FileX2, ArrowUp } from "lucide-react";
import { PaginatedResponse } from "@/types/project/pagination";
import { ProjectCardType } from "@/types/project/card";

interface ProjectExplorerProps {
  currentParams: ProjectQueryParams;
  projects: any[];
  isLoading: boolean;
  isFilterLoading: boolean;
  error: string | null;
  meta: PaginatedResponse<ProjectCardType>["meta"] | null;
  onPageChange: (page: number) => void;
  onReset: () => void;
}

export default function ProjectExplorer({
  currentParams,
  projects,
  isLoading,
  isFilterLoading,
  error,
  meta,
  onPageChange,
  onReset,
}: ProjectExplorerProps) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const isRequestingNextPageRef = useRef(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    // root: null targets the viewport, which is correct now that the page scrolls naturally

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (
          entry.isIntersecting &&
          meta?.hasNextPage &&
          !isLoading &&
          !isRequestingNextPageRef.current
        ) {
          isRequestingNextPageRef.current = true;
          observer.unobserve(sentinel);
          onPageChange((meta.currentPage || 1) + 1);
        }
      },
      { threshold: 0.1, root: null }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [meta, isLoading, onPageChange]);

  useEffect(() => {
    if (!isLoading) {
      isRequestingNextPageRef.current = false;
    }
  }, [isLoading]);

  const handleBackToTop = () => {
    onReset();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (error) {
    return (
      <div className="flex items-center justify-center py-6">
        <ErrorState
          error={error}
          description="We couldn't load the projects right now. Nothing is lost, we'll keep trying, and you can play a round while you wait."
          onRetry={onReset}
          isRetrying={isLoading}
        />
      </div>
    );
  }

  if (isFilterLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
        {Array(currentParams.limit || 9)
          .fill(0)
          .map((_, i) => (
            <Skeleton key={i} className="h-[350px] rounded-xl bg-muted" />
          ))}
      </div>
    );
  }

  if (isLoading && (!projects || projects.length === 0)) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
        {Array(currentParams.limit || 9)
          .fill(0)
          .map((_, i) => (
            <Skeleton key={i} className="h-[350px] rounded-xl bg-muted" />
          ))}
      </div>
    );
  }

  if (!projects || projects.length === 0) {
    return (
      <div className="flex items-center justify-center">
        <EmptyState
          title="No projects found"
          description="Try adjusting your filters or search criteria."
          icons={[FileX2]}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
        {projects.map((project) => {
          return (
            <div key={project.id} className="h-full">
              <Link
                href={`/project/${project.id}`}
                className="project-card group flex flex-col h-full rounded-xl overflow-hidden border bg-card shadow-sm hover:shadow-lg transition-all duration-300 ease-in-out"
              >
                <div className="relative aspect-video overflow-hidden flex-shrink-0">
                  <Image
                    src={
                      project.coverImage ||
                      "https://placehold.co/600x400?text=NO+IMAGE"
                    }
                    alt={project.title || "No title available"}
                    fill
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    className="object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  {project.status && (
                    <div className="absolute top-2 right-2 z-10">
                      <Badge>{project.status}</Badge>
                    </div>
                  )}
                </div>

                <div className="p-4 flex flex-col flex-1">
                  <h3 className="text-lg font-semibold mb-1 line-clamp-1">
                    {project.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2 flex-grow">
                    {project.subtitle || "No subtitle available."}
                  </p>

                  {project.projectTypes?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {project.projectTypes
                        .slice(0, 2)
                        .map((type: string, i: number) => (
                          <Badge
                            key={`${type}-${i}`}
                            variant="outline"
                            className="text-xs"
                          >
                            {type}
                          </Badge>
                        ))}
                      {project.projectTypes.length > 2 && (
                        <Badge variant="outline" className="text-xs">
                          +{project.projectTypes.length - 2}
                        </Badge>
                      )}
                    </div>
                  )}

                  <div className="flex justify-between items-center mt-auto pt-2 border-t border-border/50">
                    {project.domains?.length > 0 ? (
                      <div className="flex gap-1 flex-wrap">
                        {project.domains
                          .slice(0, 1)
                          .map((domain: string, i: number) => (
                            <Badge
                              key={`${domain}-${i}`}
                              variant="secondary"
                              className="text-xs"
                            >
                              {domain}
                            </Badge>
                          ))}
                        {project.domains.length > 1 && (
                          <Badge variant="secondary" className="text-xs">
                            +{project.domains.length - 1}
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <div />
                    )}

                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-xs h-7 px-2"
                    >
                      View Details
                    </Button>
                  </div>
                </div>
              </Link>
            </div>
          );
        })}
      </div>

      {isLoading && projects.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
          {Array(3)
            .fill(0)
            .map((_, i) => (
              <Skeleton key={`next-batch-${i}`} className="h-[350px] rounded-xl bg-muted" />
            ))}
        </div>
      )}

      <div ref={sentinelRef} className="h-1 w-full" />

      {!meta?.hasNextPage && projects.length > 0 && !isLoading && (
        <p className="text-center text-sm text-muted-foreground py-4">
          You&apos;ve reached the end of the results.
        </p>
      )}

      {(meta?.currentPage ?? 1) > 1 && (
        <Button
          onClick={handleBackToTop}
          size="sm"
          variant="outline"
          title="Back to top"
          className="fixed bottom-32 right-6 md:bottom-24 md:right-12 z-50 rounded-full shadow-lg h-9 w-9 p-0 !bg-white !text-black !border-white hover:!bg-white/90"
        >
          <ArrowUp className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
}